import { Button, IconButton, Tooltip } from '@material-ui/core';
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import { ClearTwoTone, Group } from '@material-ui/icons';
import { IDocumentUser } from '../../api-client/autogenerated';

export enum ImmutableUserType {
  Creator = 'CREATOR',
  Architect = 'ARCHITECT',
  Contractor = 'CONTRACTOR',
}

export enum PermissionTagType {
  Person,
  Group,
  Everyone,
}

interface PermissionTagProps {
  type: PermissionTagType;
  disableDelete?: boolean | ImmutableUserType;
  label: string;
  triggerDeletePermission?: () => void;
  onClick?: () => void;
  parentFolder?: string;
  style?: React.CSSProperties;
  disableEllipsis?: boolean;
  isDocumentDetail?: boolean;
  documentUser?: IDocumentUser;
}

const getColor = (props: PermissionTagProps) => {
  if (props.isDocumentDetail) {
    if (props.documentUser) {
      if (props.documentUser.areNotificationsDisabled) {
        return '#ed3f26';
      }
      return '#2BB073';
    }
    return '#0947B9';
  }

  return 'white';
};

const getBorderColor = (props: PermissionTagProps) => {
  if (props.documentUser) {
    if (props.documentUser.areNotificationsDisabled) {
      return '#FF5D45';
    }
    return '#2BB073';
  }
  return '#0947B9';
};

const getHoverColor = (props: PermissionTagProps) => {
  if (props.documentUser) {
    if (props.documentUser.areNotificationsDisabled) {
      return '#ed3f26';
    }
    return '#31CC85';
  }
  return '#4380ED';
};

const useStyles = makeStyles({
  rootTag: (props: PermissionTagProps) => ({
    color: getColor(props),
    borderColor: getBorderColor(props),
    // borderRadius: '24px',
    paddingLeft: '25px',
    paddingRight: '25px',
    marginLeft: props.isDocumentDetail ? 0 : 8,
    marginRight: props.isDocumentDetail ? 0 : 8,
    marginTop: 8,
    marginBottom: 8,
    whiteSpace: 'nowrap',
    maxWidth: props.isDocumentDetail ? '320px' : '250px',
    '&:hover': {
      borderColor: getHoverColor(props),
    },
  }),
  containedTag: {
    backgroundColor: '#0947B9',
    '&:hover': {
      backgroundColor: '#4380ED',
    },
    '&:active': {
      backgroundColor: '#4380ED',
    },
  },
  labelTag: {
    justifyContent: 'flex-start',
  },
  buttonLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
});

export default function PermissionTag(props: PermissionTagProps) {
  const {
    type,
    disableDelete,
    label,
    onClick,
    triggerDeletePermission,
    parentFolder,
    style,
    disableEllipsis,
    isDocumentDetail,
    documentUser,
  } = props;

  const classes = useStyles(props);

  const getStartIcon = () => {
    if (type === PermissionTagType.Person) return <PersonIcon />;
    else return <Group />;
  };

  const getTooltipTitle = () => {
    if (disableDelete && parentFolder) {
      return `Permission can be managed through parent folder: ${parentFolder}`;
    }
    if (documentUser?.areNotificationsDisabled) {
      return 'Notifications are disabled by this user for this document';
    }
    if (!disableDelete || disableDelete === true) return '';

    let str: string;
    if (disableDelete === ImmutableUserType.Contractor) {
      str = 'Responsible General Contractor';
    } else if (disableDelete === ImmutableUserType.Architect) {
      str = 'Responsible Architect';
    } else {
      str = 'Creator of this document';
    }

    return `The ${str} may not be removed from the notification list.`;
  };

  return (
    <>
      <Tooltip arrow placement="top" title={getTooltipTitle()}>
        <Button
          classes={{
            root: classes.rootTag,
            containedPrimary: classes.containedTag,
            label: isDocumentDetail ? classes.labelTag : undefined,
          }}
          style={{ paddingRight: disableDelete ? 30 : 50, ...style }}
          startIcon={getStartIcon()}
          variant={isDocumentDetail ? 'outlined' : 'contained'}
          color="primary"
          fullWidth
          onClick={onClick}
        >
          <span className={!disableEllipsis ? classes.buttonLabel : undefined}>{label}</span>
          {!disableDelete && triggerDeletePermission && (
            <IconButton
              size="small"
              style={{
                position: 'absolute',
                right: 0,
                top: 0,
                color: getColor(props),
              }}
              onClick={(e) => {
                e.stopPropagation();
                if (triggerDeletePermission) triggerDeletePermission();
              }}
            >
              <ClearTwoTone style={{ height: 20, width: 20 }} />
            </IconButton>
          )}
        </Button>
      </Tooltip>
    </>
  );
}
